import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import { useTransactions } from "../AnalyzeTransactionsTable/hooks";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { ReduxState } from "src/redux";
import { fetchMfs, fetchTls } from "src/redux/reducers/TransactionManager";
import {
  getJobDailyBalances,
  getJobDailyBalancesTagLevel,
  getJobDailyIncomeExpenses,
  getJobDailyIncomeExpensesTagLevel,
} from "src/docuclipper/api";
import { createAlert } from "src/redux/reducers/Alerts";
import { flatten, uniq } from "lodash";
import { fetchTotals } from "src/redux/reducers/Reconciler";
import AllAccountsSelector from "../AnalyzeTransactionsTable/AllAccountsSelector";

interface PLSummary {
  initialBalance: number;
  endBalance: number;
  categories: {
    [key: string]: number;
  };
}

interface MFSRawEntry {
  name: string;
  value: string;
  accountName: string;
  chunk: string;
  documentId: string;
}

interface MFSPeriod {
  startDate: string;
  endDate: string;
  startBalance: string;
  endBalance: string;
  accountName: string;
  chunk: string;
}

export const CashflowChart: React.FC = () => {
  const dispatch = useDispatch();

  const [dailyBalances, setDailyBalances] = React.useState();
  const [plSummary, setPlSummary] = React.useState<PLSummary>();
  const [incomeExpensesData, setIncomeExpensesData] = React.useState();

  const { totalsByDocumentId, allAccounts } = useSelector(
    (state: ReduxState) => state.Reconciler
  );

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const [selectedAccount, setSelectedAccount] = React.useState<string | null>(
    null
  );

  const [dateRange, setDateRange] = React.useState({
    startDate: "",
    endDate: "",
  });
  const { job } = useSelector((state: ReduxState) => state.JobData);
  React.useEffect(() => {
    dispatch(fetchTotals());
  }, []);
  React.useEffect(() => {
    if (selectedAccount) {
      const balancesFn =
        job && job.id
          ? getJobDailyBalances(
              job.id,
              selectedAccount,
              dateRange.startDate || "",
              dateRange.endDate || ""
            )
          : getJobDailyBalancesTagLevel(
              l1,
              l2,
              selectedAccount,
              dateRange.startDate || "",
              dateRange.endDate || ""
            );

      const incomeExpensesFn =
        job && job.id
          ? getJobDailyIncomeExpenses(
              job.id,
              selectedAccount,
              dateRange.startDate || "",
              dateRange.endDate || ""
            )
          : getJobDailyIncomeExpensesTagLevel(
              l1,
              l2,
              selectedAccount,
              dateRange.startDate || "",
              dateRange.endDate || ""
            );

      balancesFn
        .then(({ dailyBalances, plSummary }) => {
          setDailyBalances(dailyBalances);
          setPlSummary(plSummary);
        })
        .catch((err) =>
          dispatch(
            createAlert({
              message: err.message || "Error getting balances",
              timeout: 0,
              type: "error",
            })
          )
        );

      incomeExpensesFn
        .then(({ dailyIncomeExpenses }) => {
          setIncomeExpensesData(dailyIncomeExpenses);
        })
        .catch((err) =>
          dispatch(
            createAlert({
              message: err.message || "Error getting income/expenses data",
              timeout: 0,
              type: "error",
            })
          )
        );
    }
  }, [job?.id, dateRange?.startDate, dateRange?.endDate, selectedAccount]);

  // Initialize selectedAccount as null instead of empty string

  React.useEffect(() => {
    if (allAccounts.length > 0 && !selectedAccount) {
      setSelectedAccount(allAccounts[0]);
    }
  }, [allAccounts, selectedAccount]);

  // Add date filter state

  const handleExportToCSV = () => {
    if (!plSummary) return;

    // Prepare CSV content
    const csvContent = [
      "Balance Summary",
      `Initial Balance,${plSummary.initialBalance}`,
      `End Balance,${plSummary.endBalance}`,
      `Net Change,${plSummary.endBalance - plSummary.initialBalance}`,
      "",
      "Category Breakdown",
      "Category,Amount",
      ...Object.entries(plSummary.categories).map(
        ([category, amount]) => `${category},${amount}`
      ),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `pl_summary_${selectedAccount}.csv`;
    link.click();
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <Row className="d-flex align-items-center">
              <AllAccountsSelector
                onAccountChange={(newAccount) => setSelectedAccount(newAccount)}
              />
              <div className="d-flex align-items-center ">
                <Form.Label className="mb-0 ml-2">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: e?.target?.value,
                    }))
                  }
                  className="px-3"
                />
              </div>

              <div className="d-flex align-items-center ">
                <Form.Label className="mb-0 ml-2">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: e?.target?.value,
                    }))
                  }
                  className="px-3"
                />
              </div>
            </Row>
            {plSummary && (
              <Button variant="primary" onClick={handleExportToCSV}>
                Export to CSV
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="mb-3">
                Account Balance Over Time
              </Card.Title>
              <div style={{ width: "100%", height: "400px" }}>
                <ResponsiveContainer>
                  <LineChart
                    data={dailyBalances}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tickFormatter={(value) => {
                        try {
                          const date = new Date(value);
                          return date.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip
                      formatter={(value: any) =>
                        `$${Number(value).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke="#8884d8"
                      strokeWidth={2}
                      name="Balance"
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {plSummary && (
          <Col lg={4}>
            <Card className="mb-3 shadow-sm">
              <Card.Body>
                <Card.Title className="mb-3">Balance Summary</Card.Title>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Initial Balance</span>
                    <strong className="text-dark">
                      $
                      {plSummary.initialBalance.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">End Balance</span>
                    <strong className="text-dark">
                      $
                      {plSummary.endBalance.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold">Net Change</span>
                    <strong
                      className={`text-${
                        plSummary.endBalance - plSummary.initialBalance >= 0
                          ? "success"
                          : "danger"
                      }`}
                    >
                      $
                      {(
                        plSummary.endBalance - plSummary.initialBalance
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="mb-3">Category Breakdown</Card.Title>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {Object.entries(plSummary.categories)
                    .sort(
                      ([, a], [, b]) =>
                        Math.abs(b as number) - Math.abs(a as number)
                    )
                    .map(([category, amount]) => (
                      <div
                        key={category}
                        className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                      >
                        <span
                          className="text-truncate mr-2"
                          style={{ maxWidth: "60%" }}
                        >
                          {category}
                        </span>
                        <span
                          className={`font-weight-bold text-${
                            (amount as number) >= 0 ? "success" : "danger"
                          }`}
                        >
                          $
                          {(amount as number).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Income/Expenses Chart */}
      <Row className="mt-4">
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">
                Income/Expenses Over Time
              </Card.Title>
              <div style={{ width: "100%", height: "400px" }}>
                <ResponsiveContainer>
                  <ComposedChart
                    data={incomeExpensesData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tickFormatter={(value) => {
                        try {
                          const date = new Date(value);
                          return date.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip
                      formatter={(value: any, name: string) => [
                        `$${Math.abs(
                          typeof value === "number" ? value : Number(value) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        name,
                      ]}
                    />
                    <Legend />
                    <Bar
                      dataKey="income"
                      fill="#28a745"
                      name="Income"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="expenses"
                      fill="#dc3545"
                      name="Expenses"
                      radius={[0, 0, 4, 4]}
                    />
                    <Line
                      type="monotone"
                      dataKey="netFlow"
                      stroke="#007bff"
                      strokeWidth={3}
                      name="Net Flow"
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
